import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MatchEvent } from "../../../store/useMatchLogStore";
import { ACTION_FLOWS } from "../constants";
import {
  ActionStep,
  EventType,
  FieldAnchor,
  FieldCoordinate,
  IneffectiveAction,
  Match,
  Player,
  Team,
} from "../types";
import { ZONES, ZONE_W, ZONE_H, locationToZoneId } from "../utils/heatMapZones";

export type PositionMode = "manual" | "auto";

interface UseActionFlowParams {
  match: Match | null;
  globalClock: string;
  operatorPeriod: number;
  selectedTeam: "home" | "away" | "both";
  isSubmitting: boolean;
  expelledPlayerIds: Set<string>;
  recentEvents: MatchEvent[];
  onIneffectiveTrigger?: (payload: {
    note?: string;
    teamId: string;
    playerId?: string;
    actionType: IneffectiveAction;
  }) => void;
  sendEvent: (event: Omit<MatchEvent, "match_id" | "timestamp">) => void;
}

const resolveEventType = (action: string): EventType => {
  if (action === "Pass") return "Pass";
  if (action === "Shot" || action === "Goal" || action === "DirectShot")
    return "Shot";
  if (action === "2PT" || action === "3PT" || action === "FreeThrow")
    return "Shot";
  if (action === "Duel") return "Duel";
  if (action === "Foul") return "FoulCommitted";
  if (action === "Ejected") return "Ejected";
  if (action === "Disqualified") return "Disqualified";
  if (action === "Interception") return "Interception";
  if (action === "Block") return "Block";
  if (action === "Recovery") return "Recovery";
  if (action === "Assist") return "Assist";
  if (action === "Rebound") return "Rebound";
  if (action === "Steal") return "Steal";
  if (action === "Turnover") return "Turnover";
  if (action === "Violation") return "Violation";
  if (action === "JumpBall") return "JumpBall";
  if (action === "ReviewDecision") return "ReviewDecision";
  if (action === "FreeThrow") return "Shot";
  if (action === "Tip Off") return "JumpBall";
  return "Pass";
};

const getActionConfig = (action?: string | null) => {
  if (!action) return undefined;
  return Object.values(ACTION_FLOWS).find((config) =>
    config.actions.includes(action),
  );
};

const buildEventPayload = (
  action: string,
  outcome: string | null,
  recipient: Player | null,
  currentTeam: Team,
  selectedPlayer: Player,
  globalClock: string,
  operatorPeriod: number,
  location?: [number, number],
  endLocation?: [number, number],
  extraData?: Record<string, any>,
  zoneId?: number | null,
): Omit<MatchEvent, "match_id" | "timestamp"> => {
  const eventType = resolveEventType(action);
  // Always use the live global clock for event timestamps to avoid stale operator-clock values.
  const matchClock = globalClock;

  const eventData: Omit<MatchEvent, "match_id" | "timestamp"> = {
    match_clock: matchClock,
    period: operatorPeriod,
    team_id: currentTeam.id,
    player_id: selectedPlayer.id,
    type: eventType,
    data: {},
  };

  if (location) {
    eventData.location = location;
  }

  switch (eventType) {
    case "Pass":
      eventData.data = {
        pass_type: "Standard",
        outcome: outcome || "Complete",
        receiver_id: recipient?.id,
        receiver_name: recipient?.full_name,
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Shot": {
      // Determine shot_type from the original action name
      let shotType = "2pt";
      if (action === "3PT") shotType = "3pt";
      else if (action === "FreeThrow") shotType = "ft";
      else if (action === "Shot" || action === "Goal" || action === "DirectShot") shotType = "2pt";
      else if (action === "2PT") shotType = "2pt";
      eventData.data = {
        shot_type: shotType,
        outcome: outcome || "Made",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    }
    case "Assist":
      eventData.data = {
        assist_type: "Standard",
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Duel":
      eventData.data = {
        duel_type: "Ground",
        outcome: outcome || "Won",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "FoulCommitted":
      eventData.data = {
        foul_type: "Standard",
        outcome: outcome || "Standard",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Ejected":
      eventData.data = {
        reason: outcome || "Ejected",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Disqualified":
      eventData.data = {
        reason: outcome || "Disqualified",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Interception":
      eventData.data = {
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Block":
      eventData.data = {
        block_type: "Shot",
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Recovery":
      eventData.data = {
        recovery_type: outcome || "Loose Ball",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Rebound":
      eventData.data = {
        rebound_type: outcome || "Defensive",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Steal":
      eventData.data = {
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Turnover":
      eventData.data = {
        turnover_type: outcome || "BadPass",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Violation":
      eventData.data = {
        violation_type: action,
        outcome: outcome || "Standard",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "JumpBall":
      eventData.data = {
        outcome: outcome || "Won",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "ReviewDecision":
      eventData.data = {
        decision: outcome || "Confirmed",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    default:
      break;
  }

  if (extraData) {
    eventData.data = { ...eventData.data, ...extraData };
  }

  if (zoneId != null) {
    eventData.data.zone_id = zoneId;
  }

  return eventData;
};

export const useActionFlow = ({
  match,
  globalClock,
  operatorPeriod,
  selectedTeam,
  isSubmitting,
  expelledPlayerIds,
  recentEvents,
  onIneffectiveTrigger,
  sendEvent,
}: UseActionFlowParams) => {
  const [currentStep, setCurrentStep] = useState<ActionStep>("selectPlayer");
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [pendingOutcome, setPendingOutcome] = useState<string | null>(null);
  const [fieldAnchor, setFieldAnchor] = useState<FieldAnchor | null>(null);
  const [selectedPlayerLocation, setSelectedPlayerLocation] = useState<
    [number, number] | null
  >(null);
  /** Exact StatsBomb coords from the player-node click (before zone quantisation). */
  const [playerClickLocation, setPlayerClickLocation] = useState<
    [number, number] | null
  >(null);
  /** Zone the operator confirmed – attached as data.zone_id on every event. */
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  /** Position assignment mode: manual (zone selector) or auto (use node coords). */
  const [positionMode, setPositionMode] = useState<PositionMode>("manual");
  const currentStepRef = useRef<ActionStep>("selectPlayer");

  const currentTeam = useMemo<Team | undefined>(() => {
    if (!match) return undefined;
    if (selectedPlayer) {
      const isHomePlayer = match.home_team.players.some(
        (player) => player.id === selectedPlayer.id,
      );
      if (isHomePlayer) return match.home_team;
      const isAwayPlayer = match.away_team.players.some(
        (player) => player.id === selectedPlayer.id,
      );
      if (isAwayPlayer) return match.away_team;
    }
    if (selectedTeam === "home") return match.home_team;
    if (selectedTeam === "away") return match.away_team;
    return undefined;
  }, [match, selectedPlayer, selectedTeam]);

  const resolvePlayerSide = useCallback(
    (player?: Player | null): "home" | "away" | null => {
      if (!match || !player) return null;
      if (match.home_team.players.some((p) => p.id === player.id))
        return "home";
      if (match.away_team.players.some((p) => p.id === player.id))
        return "away";
      return null;
    },
    [match],
  );

  const resolveOpponentTeamId = useCallback(
    (teamId?: string | null): string | null => {
      if (!match || !teamId) return null;
      if (teamId === match.home_team.id) return match.away_team.id;
      if (teamId === match.away_team.id) return match.home_team.id;
      return null;
    },
    [match],
  );

  const availableActions = useMemo(() => {
    if (!selectedPlayer) return [];
    return Object.values(ACTION_FLOWS).flatMap((config) => config.actions);
  }, [selectedPlayer]);

  const availableOutcomes = useMemo(() => {
    if (!selectedAction) return [];
    return getActionConfig(selectedAction)?.outcomes?.[selectedAction] || [];
  }, [selectedAction]);

  const resetFlow = useCallback(() => {
    setCurrentStep("selectPlayer");
    setSelectedPlayer(null);
    setSelectedAction(null);
    setPendingOutcome(null);
    setFieldAnchor(null);
    setSelectedPlayerLocation(null);
    setPlayerClickLocation(null);
    setSelectedZoneId(null);
  }, []);

  const dispatchEvent = useCallback(
    (
      action: string,
      outcome: string | null,
      recipient: Player | null,
      opts?: {
        location?: [number, number];
        endLocation?: [number, number];
        extraData?: Record<string, any>;
      },
    ) => {
      if (!match || !currentTeam || !selectedPlayer) return false;
      if (isSubmitting) return false;
      if (expelledPlayerIds.has(selectedPlayer.id)) return false;
      const resolvedOutcome = outcome;

      const payload = buildEventPayload(
        action,
        resolvedOutcome,
        recipient,
        currentTeam,
        selectedPlayer,
        globalClock,
        operatorPeriod,
        opts?.location,
        opts?.endLocation,
        opts?.extraData,
        selectedZoneId,
      );
      sendEvent(payload);

      if (action === "Foul") {
        onIneffectiveTrigger?.({
          teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
          playerId: selectedPlayer.id,
          actionType: "Foul",
        });
      }
      if (action === "Violation") {
        onIneffectiveTrigger?.({
          teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
          playerId: selectedPlayer.id,
          actionType: "Violation",
        });
      }
      return true;
    },
    [
      match,
      currentTeam,
      selectedPlayer,
      selectedZoneId,
      isSubmitting,
      expelledPlayerIds,
      recentEvents,
      onIneffectiveTrigger,
      resolveOpponentTeamId,
      globalClock,
      operatorPeriod,
      sendEvent,
    ],
  );

  const handlePlayerClick = useCallback(
    (player: Player, anchor?: FieldAnchor, location?: [number, number]) => {
      if (expelledPlayerIds.has(player.id)) {
        return;
      }
      setSelectedPlayer(player);
      // Store the exact position from the player node; zone selection will
      // decide whether to keep it (if it falls inside the chosen zone) or
      // fall back to the zone centre.
      setPlayerClickLocation(location ?? null);
      setSelectedPlayerLocation(null);
      setFieldAnchor(anchor ?? null);

      if (positionMode === "auto" && location) {
        // Auto mode: derive zone from player node coords, skip zone selector.
        const autoZoneId = locationToZoneId(location[0], location[1]);
        setSelectedZoneId(autoZoneId);
        setSelectedPlayerLocation(location);
        setCurrentStep(anchor ? "selectQuickAction" : "selectAction");
      } else {
        setCurrentStep("selectZone");
      }
    },
    [expelledPlayerIds, positionMode],
  );

  const handleZoneSelect = useCallback(
    (zoneId: number) => {
      const zone = ZONES[zoneId];
      if (!zone) return;

      // Zone-biased positioning: use the player's exact click location when
      // it falls inside the zone the operator confirmed.  Otherwise, fall
      // back to the zone centre (the operator deliberately corrected the
      // position).
      let location: [number, number];
      if (
        playerClickLocation &&
        playerClickLocation[0] >= zone.x0 &&
        playerClickLocation[0] <= zone.x1 &&
        playerClickLocation[1] >= zone.y0 &&
        playerClickLocation[1] <= zone.y1
      ) {
        location = playerClickLocation;
      } else {
        location = [zone.x0 + ZONE_W / 2, zone.y0 + ZONE_H / 2];
      }

      setSelectedZoneId(zoneId);
      setSelectedPlayerLocation(location);
      setCurrentStep(fieldAnchor ? "selectQuickAction" : "selectAction");
    },
    [fieldAnchor, playerClickLocation],
  );

  const handleQuickActionSelect = useCallback(
    (action: string) => {
      setSelectedAction(action);
      setPendingOutcome(null);
      if (action === "Foul") {
        setCurrentStep("selectOutcome");
        return;
      }

      // Ejected / Disqualified — complete immediately
      if (action === "Ejected" || action === "Disqualified") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, action, null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            resetFlow();
          }
        }
        return;
      }

      // Basketball shot types → go to outcome selection
      if (action === "2PT" || action === "3PT" || action === "FreeThrow") {
        setCurrentStep("selectOutcome");
        return;
      }
      if (action === "DirectShot") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, "OnTarget", null, {
            location: selectedPlayerLocation ?? undefined,
            extraData: {
              shot_type: "Direct",
            },
          });
          if (sent) {
            resetFlow();
          }
        }
        return;
      }
      if (action === "Goal") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, "Goal", null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            resetFlow();
          }
        }
        return;
      }

      // Basketball quick actions that complete immediately (no destination needed)
      const immediateActions = [
        "Steal",
        "Rebound",
        "Turnover",
        "Block",
        "Assist",
        "Violation",
      ];
      if (immediateActions.includes(action)) {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, null, null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            resetFlow();
          }
        }
        return;
      }

      setCurrentStep("selectDestination");
    },
    [
      currentTeam,
      dispatchEvent,
      resetFlow,
      selectedPlayer,
      selectedPlayerLocation,
    ],
  );

  const handleOpenMoreActions = useCallback(() => {
    setCurrentStep("selectAction");
  }, []);

  const handleActionClick = useCallback((action: string) => {
    setSelectedAction(action);
    setPendingOutcome(null);
    setCurrentStep("selectOutcome");
  }, []);

  const handleDestinationClick = useCallback(
    (payload: {
      destination: FieldCoordinate;
      targetPlayer?: Player | null;
    }) => {
      if (!selectedAction || !selectedPlayer || !currentTeam) {
        return { sent: false, outOfBounds: false, isGoal: false };
      }

      const destination = payload.destination;
      const targetPlayer = payload.targetPlayer ?? null;
      const selectedPlayerSide = resolvePlayerSide(selectedPlayer);
      const targetPlayerSide = resolvePlayerSide(targetPlayer);
      const isSameTeam =
        selectedPlayerSide && targetPlayerSide
          ? selectedPlayerSide === targetPlayerSide
          : false;
      const isOpponent =
        selectedPlayerSide && targetPlayerSide
          ? selectedPlayerSide !== targetPlayerSide
          : false;

      let outcome: string | null = null;
      const extraData: Record<string, any> = {
        destination_type: destination.isOutOfBounds
          ? "out_of_bounds"
          : targetPlayer
            ? isSameTeam
              ? "teammate"
              : "opponent"
            : "empty",
        out_of_bounds: destination.isOutOfBounds,
      };

      if (targetPlayer) {
        extraData.target_player_id = targetPlayer.id;
        extraData.target_player_name = targetPlayer.full_name;
      }

      if (selectedAction === "Pass") {
        if (destination.isOutOfBounds) {
          outcome = "Out";
        } else if (isSameTeam) {
          outcome = "Complete";
        } else {
          outcome = "Incomplete";
          if (isOpponent && targetPlayer) {
            extraData.intercepted_by_id = targetPlayer.id;
            extraData.intercepted_by_name = targetPlayer.full_name;
          }
        }
      } else if (
        selectedAction === "Shot" ||
        selectedAction === "Goal" ||
        selectedAction === "DirectShot"
      ) {
        if (destination.isOutOfBounds) {
          outcome = "Missed";
        } else if (isOpponent) {
          outcome = "Blocked";
        } else {
          outcome = selectedAction === "Goal" ? "Goal" : "Missed";
        }
      } else if (selectedAction === "Foul") {
        outcome = "Standard";
      } else if (selectedAction === "Duel") {
        outcome = isOpponent ? "Lost" : "Won";
      } else if (selectedAction === "Card") {
        outcome = null;
      }

      const recipient = isSameTeam ? targetPlayer : null;
      const sent = dispatchEvent(selectedAction, outcome, recipient, {
        location: selectedPlayerLocation ?? undefined,
        endLocation: destination.statsbomb,
        extraData,
      });

      const isGoal = outcome === "Goal";

      const triggerAction: IneffectiveAction | null = isGoal
        ? "Goal"
        : destination.isOutOfBounds
          ? "OutOfBounds"
          : null;

      const triggerContext =
        sent && triggerAction
          ? {
              actionType: triggerAction,
              teamId:
                triggerAction === "OutOfBounds"
                  ? resolveOpponentTeamId(currentTeam.id) ||
                    currentTeam.id
                  : currentTeam.id,
              playerId: selectedPlayer.id,
            }
          : null;

      if (sent) {
        resetFlow();
      }

      return {
        sent,
        outOfBounds: destination.isOutOfBounds,
        isGoal,
        triggerContext,
      };
    },
    [
      globalClock,
      match,
      currentTeam,
      dispatchEvent,
      operatorPeriod,
      resetFlow,
      resolvePlayerSide,
      sendEvent,
      selectedAction,
      selectedPlayer,
      selectedPlayerLocation,
    ],
  );

  const handleOutcomeClick = useCallback(
    (outcome: string) => {
      if (!selectedAction) return;
      const config = getActionConfig(selectedAction);
      console.log("[useActionFlow] handleOutcomeClick:", {
        outcome,
        selectedAction,
        config,
        needsRecipient: config?.needsRecipient,
      });
      if (selectedAction === "Pass" && outcome === "OutOfBounds") {
        const sent = dispatchEvent(selectedAction, outcome, null, {
          location: selectedPlayerLocation ?? undefined,
          extraData: {
            destination_type: "out_of_bounds",
            out_of_bounds: true,
          },
        });
        if (sent && currentTeam && selectedPlayer) {
          onIneffectiveTrigger?.({
            note: "Out of bounds",
            teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
            playerId: selectedPlayer.id,
            actionType: "OutOfBounds",
          });
          resetFlow();
        }
        return;
      }
      if (config?.needsRecipient) {
        setPendingOutcome(outcome);
        setCurrentStep("selectRecipient");
        console.log("[useActionFlow] Transitioning to selectRecipient step");
        return;
      }
      if (dispatchEvent(selectedAction, outcome, null)) {
        resetFlow();
      }
    },
    [
      dispatchEvent,
      currentTeam,
      onIneffectiveTrigger,
      resetFlow,
      selectedAction,
      selectedPlayer,
      selectedPlayerLocation,
      currentTeam?.players,
      resolveOpponentTeamId,
    ],
  );

  const handleRecipientClick = useCallback(
    (recipient: Player) => {
      if (!selectedAction || !pendingOutcome) return;
      if (dispatchEvent(selectedAction, pendingOutcome, recipient)) {
        resetFlow();
      }
    },
    [dispatchEvent, pendingOutcome, resetFlow, selectedAction],
  );

  useEffect(() => {
    currentStepRef.current = currentStep;
    console.log("[useActionFlow] currentStep changed to:", currentStep);
  }, [currentStep]);

  return {
    currentStep,
    currentTeam,
    selectedPlayer,
    selectedAction,
    selectedZoneId,
    positionMode,
    setPositionMode,
    fieldAnchor,
    availableActions,
    availableOutcomes,
    handlePlayerClick,
    handleZoneSelect,
    handleQuickActionSelect,
    handleOpenMoreActions,
    handleDestinationClick,
    handleActionClick,
    handleOutcomeClick,
    handleRecipientClick,
    resetFlow,
    currentStepRef,
  } as const;
};
